import { TOOL_TYPE } from 'core/chatbot/utils/parsers/ToolType';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChatMessage from '../ChatMessage/ChatMessage';
import TypingIndicator from '../TypingIndicator/TypingIndicator';
import './ChatMessages.scss';

// Converting to a memo component to prevent unnecessary re-renders
const ChatMessages = React.memo(function ChatMessages({ messages, streamingMessage, activeToolEvents, showDebugTools, mode, isLoading }) {
  const { t } = useTranslation(['chatbot']);
  const messagesEndRef = useRef(null);
  const [expandedContextId, setExpandedContextId] = useState(null);
  const containerRef = useRef(null);
  const isStreamingRef = useRef(false);

  const scrollToBottom = useCallback((isStreaming = false) => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    // Use scrollHeight to account for ALL content including tool indicators
    if (isStreaming) {
      container.scrollTop = container.scrollHeight;
    } else {
      // Smooth scroll for normal messages
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, []);

  // Efecto para manejar el scroll durante el streaming
  useEffect(() => {
    if (streamingMessage?.isStreaming) {
      isStreamingRef.current = true;
      requestAnimationFrame(() => {
        scrollToBottom(true);
      });
    } else {
      isStreamingRef.current = false;
    }
  }, [streamingMessage?.text, activeToolEvents, scrollToBottom]);

  // Efecto para manejar el scroll de mensajes normales
  useLayoutEffect(() => {
    if (!isStreamingRef.current) {
      requestAnimationFrame(() => {
        scrollToBottom(true);
      });
    }
  }, [messages, activeToolEvents, scrollToBottom]);

  // Handle context expansion toggle
  const handleToggleContext = (messageId) => {
    setExpandedContextId((current) => (current === messageId ? null : messageId));
  };

  // Show loading indicator while conversation is loading
  if (isLoading) {
    return (
      <div className="chat-messages chat-messages--loading" ref={containerRef}>
        <div className="chat-messages__container">
          <div className="chat-messages__loading">
            <TypingIndicator />
          </div>
        </div>
      </div>
    );
  }

  // Create welcome message that always appears first
  const welcomeMessage = {
    id: 'welcome-message',
    sender: 'bot',
    text: t('Welcome message'),
    timestamp: new Date(),
    isError: false,
    isStreaming: false,
    justPosted: false,
    context: null,
  };

  // Debug logging for teacher-tools mode
  if (mode === 'teacher-tools') {
    console.log('[ChatMessages] Incoming messages:', messages);
    console.log('[ChatMessages] Mode:', mode);
  }

  // Build complete list of messages starting with welcome message (only in study mode)
  // Filter out empty user messages (text-less, image-less, attachment-less)
  const allMessages = [
    ...(mode === 'study' ? [welcomeMessage] : []),
    ...messages.filter((msg) => msg.sender === 'bot' || (msg.text && msg.text.trim()) || msg.image || (msg.attachments && msg.attachments.length > 0)),
  ];

  // Debug logging for teacher-tools mode
  if (mode === 'teacher-tools') {
    console.log('[ChatMessages] allMessages after filter:', allMessages);
    console.log('[ChatMessages] Filter removed', messages.length - allMessages.length, 'messages');
  }

  // Add streaming message if present
  if (streamingMessage) {
    const completeStreamingMessage = {
      ...streamingMessage,
      expandedContext: expandedContextId === streamingMessage.id,
      onToggleContext: () => handleToggleContext(streamingMessage.id),
    };
    allMessages.push(completeStreamingMessage);
  }

  const activeTools =
    activeToolEvents &&
    activeToolEvents.length > 0 &&
    activeToolEvents
      .filter(
        (event) =>
          event.toolType === TOOL_TYPE.MERMAID_DIAGRAM ||
          event.toolType === TOOL_TYPE.D3_VISUALIZATION ||
          event.toolType === TOOL_TYPE.GEOGEBRA_VISUALIZATION ||
          event.toolType === TOOL_TYPE.THREEJS_VISUALIZATION ||
          event.toolType === TOOL_TYPE.CANVAS_GENERATION
      )
      .filter((event, index, self) => index === self.findIndex((e) => e.toolName === event.toolName));

  return (
    <div className="chat-messages" ref={containerRef}>
      <div className="chat-messages__container">
        {allMessages.map((message, index) => (
          <ChatMessage
            key={message.id}
            message={{
              id: message.id,
              sender: message.sender,
              text: message.text,
              timestamp: message.timestamp,
              isError: message.isError,
              isFlagged: message.isFlagged,
              isStreaming: message.isStreaming,
              justPosted: message.justPosted,
              context: showDebugTools ? message.context : null,
              image: message.image,
              imageName: message.imageName,
              attachments: message.attachments,
            }}
            activeTools={index === allMessages.length - 1 ? activeTools : []}
            className=""
          />
        ))}

        <div ref={messagesEndRef} />
      </div>
    </div>
  );
});

ChatMessages.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      sender: PropTypes.oneOf(['user', 'bot']).isRequired,
      text: PropTypes.string.isRequired,
      timestamp: PropTypes.instanceOf(Date),
      isError: PropTypes.bool,
      context: PropTypes.object,
      image: PropTypes.string,
      imageName: PropTypes.string,
      attachments: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string.isRequired,
          contentType: PropTypes.string.isRequired,
          fileName: PropTypes.string.isRequired,
          sizeInBytes: PropTypes.number.isRequired,
          createdAt: PropTypes.string.isRequired,
        })
      ),
    })
  ),
  streamingMessage: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    sender: PropTypes.oneOf(['user', 'bot']).isRequired,
    text: PropTypes.string.isRequired,
    timestamp: PropTypes.instanceOf(Date),
    isStreaming: PropTypes.bool,
    isError: PropTypes.bool,
    context: PropTypes.object,
    image: PropTypes.string,
    imageName: PropTypes.string,
    attachments: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string.isRequired,
        contentType: PropTypes.string.isRequired,
        fileName: PropTypes.string.isRequired,
        sizeInBytes: PropTypes.number.isRequired,
        createdAt: PropTypes.string.isRequired,
      })
    ),
  }),
  activeToolEvents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.oneOf(['tool_invocation', 'tool_completion']),
      toolName: PropTypes.string,
      toolType: PropTypes.oneOf([
        'd3_visualization',
        'mermaid_diagram',
        'content_retrieval',
        'geogebra_visualization',
        'threejs_visualization',
        'web_search',
        'canvas_generation',
        'unknown',
      ]),
      status: PropTypes.oneOf(['started', 'completed', 'failed']),
      timestamp: PropTypes.string,
      args: PropTypes.any,
      result: PropTypes.any,
    })
  ),
  showDebugTools: PropTypes.bool,
  mode: PropTypes.oneOf(['study', 'practice', 'teacher-tools', 'revision']),
  isLoading: PropTypes.bool,
};

ChatMessages.defaultProps = {
  messages: [],
  streamingMessage: null,
  activeToolEvents: [],
  showDebugTools: false,
  mode: 'study',
  isLoading: false,
};

export default ChatMessages;
